import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { DEFAULT_RELEASE_KIT_LOOK, ReleaseKitLook } from 'fym-shared/src/music24/releaseThemes';
import { useGetReleaseKitLookQuery, useUpdateKitLookMutation } from '../../../../../redux/api/fymAPI';
import { SerializedKit } from '../../../../../redux/api/fymAPI.types';
import { useShowToast } from '../../../../../redux/slices/toasts/hooks';
import { themeMessages } from './themeMessages';

interface UseKitLookProps {
  accountId: number;
  catalogItemUuid: string;
  kit: SerializedKit | null;
}

export interface KitLookState {
  /** The look on screen: the kit's look with the change that is being saved on top. */
  look: ReleaseKitLook;
  saveAsDefault: boolean;
  isSaving: boolean;
  /**
   * The sentence of the last change that did not save. The panel shows it next to the theme
   * choices (not in a toast at the top of the page) until the next change.
   */
  saveError: string | null;
  change: (patch: Partial<ReleaseKitLook>) => void;
  toggleSaveAsDefault: () => void;
}

type LookPatch = Partial<ReleaseKitLook>;

interface QueuedPatch {
  patch: LookPatch;
  asDefault: boolean;
}

/**
 * Removes from `pending` the keys that a finished PATCH sent, but only while they still hold the
 * value it sent. A newer change of the same key stays on screen until its own PATCH ends.
 */
export function clearSentKeys(pending: LookPatch, sent: LookPatch): LookPatch {
  const next: LookPatch = { ...pending };
  (Object.keys(sent) as Array<keyof ReleaseKitLook>).forEach((key) => {
    if (next[key] === sent[key]) {
      delete next[key];
    }
  });
  return next;
}

/**
 * Removes from `pending` the keys that no PATCH sends any more and that the kit shows: the keys
 * that hold the value of `base`, or every such key when `base` is a new look (the kit was read
 * again after the PATCH, so the server's value wins).
 */
export function clearShownKeys(
  pending: LookPatch,
  base: ReleaseKitLook,
  busyKeys: ReadonlySet<string>,
  baseChanged: boolean
): LookPatch {
  const next: LookPatch = { ...pending };
  (Object.keys(next) as Array<keyof ReleaseKitLook>).forEach((key) => {
    if (!busyKeys.has(key) && (baseChanged || next[key] === base[key])) {
      delete next[key];
    }
  });
  return Object.keys(next).length === Object.keys(pending).length ? pending : next;
}

/**
 * The look of one song: the release theme, the font override and the logo. A kit holds its own
 * look; with no kit yet the panel shows the artist's default (`GET .../release-kit-look`), else
 * Clean with the theme font and no logo, and the first
 * change makes the kit (the server starts it from the same default). Every change is one
 * `PATCH .../look` with only the keys that changed. The PATCHes go one at a time: a change made
 * while one is on its way waits and merges with the next changes, so the newest value always
 * lands last. A saved change stays on screen until the kit shows it: the PATCH makes the song
 * page read the kit again, and while that read runs the query still gives the old look. Dropping
 * the change when the PATCH ends showed the old choice again for a moment (the selection blinked).
 * While "Save as my default" is on, each change also writes the default
 * (`saveAsDefault: true`).
 */
export function useKitLook({ accountId, catalogItemUuid, kit }: UseKitLookProps): KitLookState {
  const intl = useIntl();
  const { showSuccess } = useShowToast();
  const savedDefault = useGetReleaseKitLookQuery({ accountId }, { skip: kit !== null });
  const [updateKitLook, { isLoading }] = useUpdateKitLookMutation();
  const [pending, setPending] = useState<LookPatch>({});
  const [saveAsDefault, setSaveAsDefault] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const sending = useRef<LookPatch | null>(null);
  const queued = useRef<QueuedPatch | null>(null);

  const base = kit?.look ?? savedDefault.data?.look ?? DEFAULT_RELEASE_KIT_LOOK;
  const look: ReleaseKitLook = useMemo(() => ({ ...base, ...pending }), [base, pending]);

  const baseRef = useRef(base);
  const shownBase = useRef(base);
  baseRef.current = base;
  // Drops the saved changes that the kit shows. It runs when the kit look changes and when a PATCH ends.
  const settle = useCallback(() => {
    const baseChanged = shownBase.current !== baseRef.current;
    shownBase.current = baseRef.current;
    const busyKeys = new Set([...Object.keys(sending.current ?? {}), ...Object.keys(queued.current?.patch ?? {})]);
    setPending((current) => clearShownKeys(current, baseRef.current, busyKeys, baseChanged));
  }, []);
  useEffect(settle, [base, settle]);

  // The PATCH that ends sends the queued one through this ref, so it always calls the newest `sendNow`.
  const sendNowRef = useRef<(next: QueuedPatch) => void>(() => undefined);
  const sendNow = useCallback(
    ({ patch, asDefault }: QueuedPatch): void => {
      sending.current = patch;
      updateKitLook({ accountId, catalogItemUuid, ...patch, saveAsDefault: asDefault })
        .unwrap()
        .then(() => {
          if (asDefault) {
            showSuccess(intl.formatMessage(themeMessages.saved));
          }
        })
        .catch((error: unknown) => {
          console.error('Failed to save the theme of the song', error);
          setSaveError(intl.formatMessage(themeMessages.saveFailed));
          // The change did not save: the kit look comes back, except for a key that the next PATCH sends again.
          const failed = { ...patch };
          Object.keys(queued.current?.patch ?? {}).forEach((key) => {
            delete failed[key as keyof ReleaseKitLook];
          });
          setPending((current) => clearSentKeys(current, failed));
        })
        .finally(() => {
          sending.current = null;
          const next = queued.current;
          queued.current = null;
          if (next !== null) {
            sendNowRef.current(next);
          }
          settle();
        });
    },
    [accountId, catalogItemUuid, intl, settle, showSuccess, updateKitLook]
  );
  sendNowRef.current = sendNow;

  const send = useCallback(
    (patch: LookPatch, asDefault: boolean) => {
      if (sending.current === null) {
        sendNow({ patch, asDefault });
        return;
      }
      const waiting = queued.current;
      queued.current = {
        patch: { ...waiting?.patch, ...patch },
        asDefault: (waiting?.asDefault ?? false) || asDefault,
      };
    },
    [sendNow]
  );

  const change = useCallback(
    (patch: LookPatch) => {
      setSaveError(null);
      setPending((current) => ({ ...current, ...patch }));
      send(patch, saveAsDefault);
    },
    [saveAsDefault, send]
  );

  const toggleSaveAsDefault = useCallback(() => {
    const next = !saveAsDefault;
    setSaveAsDefault(next);
    if (next) {
      // The changes still on screen go with it, so the default holds the look the user sees.
      send({ ...pending }, true);
    }
  }, [pending, saveAsDefault, send]);

  return { look, saveAsDefault, isSaving: isLoading, saveError, change, toggleSaveAsDefault };
}
